/**
 * One logged set, written in whatever the exercise actually measures.
 *
 * Every screen that lists sets goes through here. The alternative — each screen
 * deciding for itself — is how "5 × —" ends up rendered for a plank on the
 * history tab while the log tab gets it right.
 */

import { measuresOf, type Exercise, type SetRow } from '../db'
import {
  formatDistance,
  formatDuration,
  formatPace,
  formatWeight,
  type DistanceUnit,
  type Unit,
} from './units'

export function formatSet(
  set: SetRow,
  exercise: Exercise | undefined,
  unit: Unit,
  opts: { withRpe?: boolean; withWarmup?: boolean; du?: DistanceUnit } = {},
): string {
  const ms = measuresOf(exercise)
  // Defaulting rather than requiring: every caller that renders a distance
  // passes it, and the ones that never show one (a bench press row) should not
  // have to care.
  const du = opts.du ?? 'km'
  const parts: string[] = []

  if (ms.includes('reps') && set.reps != null) {
    // Weight × reps keeps its familiar shape; bodyweight reps say so rather
    // than rendering "8 × —".
    parts.push(
      ms.includes('weight') && set.weight_kg != null
        ? `${set.reps} × ${formatWeight(set.weight_kg, unit, { withUnit: false })}`
        : `${set.reps} reps`,
    )
  } else if (ms.includes('weight') && set.weight_kg != null) {
    parts.push(formatWeight(set.weight_kg, unit))
  }

  if (ms.includes('distance') && set.distance_m != null) {
    parts.push(formatDistance(set.distance_m, du))
  }
  if (ms.includes('time') && set.duration_seconds != null) {
    parts.push(formatDuration(set.duration_seconds))
  }
  if (ms.includes('distance') && ms.includes('time') && set.distance_m && set.duration_seconds) {
    parts.push(formatPace(set.distance_m, set.duration_seconds, du))
  }

  if (parts.length === 0) return '—'

  let text = parts.join(' · ')
  // Off where the caller already labels warmups itself, so they don't get
  // marked twice.
  if (set.is_warmup && opts.withWarmup !== false) text += ' (w)'
  if (opts.withRpe !== false && set.rpe != null) text += ` @ RPE ${set.rpe}`
  return text
}
